import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getIdeaById, updateIdeaStatus, assignManagers,
  addComment, rateIdea, flagIdea, deleteIdea,
} from '../../services/idea.service';
import { getManagers } from '../../services/user.service';
import {
  getEffectiveUserRole, ROLE_COMPONENT_IDS,
  getStoredUserEmail,
} from '../../services/auth.service';
import { canPerformAction } from '../../constants/ideaWorkflow';
import RoleViewTabs from './shared/RoleViewTabs';
import IdeaStatusBadge from './shared/IdeaStatusBadge';
import { checkFeasibility, parseFeasibilityResult } from '../../services/feasibility.service';
import { generateReport } from '../../services/agentcore.service';
import { showSuccess, showError } from '../../services/notification.service';
import './IdeaDetail.css';

const QUESTIONS = [
  { id: 'q1', question: '1. What problem does your idea solve?' },
  { id: 'q2', question: '2. Who is your target audience / customer?' },
  { id: 'q3', question: '3. What makes your solution unique?' },
  { id: 'q4', question: '4. What is your revenue model?' },
  { id: 'q5', question: '5. What are the key technical requirements?' },
  { id: 'q6', question: '6. What is your go-to-market strategy?' },
  { id: 'q7', question: '7. What are the major risks and challenges?' },
  { id: 'q8', question: '8. What is your expected timeline and budget?' },
];

function IdeaDetail() {
  const { ideaId } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(getEffectiveUserRole());
  const userEmail = getStoredUserEmail();

  const isAdmin   = role === ROLE_COMPONENT_IDS.ADMIN;
  const isManager = role === ROLE_COMPONENT_IDS.MANAGER;
  const isOwner   = role === ROLE_COMPONENT_IDS.IDEA_OWNER;

  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Comment state
  const [commentText, setCommentText] = useState('');
  const [commentVisibility, setCommentVisibility] = useState('all');
  const [postingComment, setPostingComment] = useState(false);

  // Rating state
  const [rating, setRating] = useState({ technical_feasibility: 3, business_viability: 3, impact: 3, notes: '' });
  const [savingRating, setSavingRating] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState(false);

  // Modals
  const [statusModal, setStatusModal] = useState(null); // { newStatus, reason }
  const [assignModal, setAssignModal] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);

  const reload = async () => {
    try {
      const data = await getIdeaById(ideaId);
      if (data.success) setIdea(data.idea);
    } catch {
      showError('Failed to reload idea');
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getIdeaById(ideaId);
        if (data.success) {
          setIdea(data.idea);
          setSelectedManagers(data.idea.assigned_managers || []);
        }
      } catch {
        showError('Idea not found or access denied');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ideaId, role]);

  // Load managers when admin opens assign modal
  useEffect(() => {
    if (assignModal && isAdmin && managers.length === 0) {
      getManagers()
        .then((d) => {
          const rows = Array.isArray(d) ? d : (d.managers || []);
          const dedup = new Map();
          rows.forEach((m) => {
            const email = String(m?.email || '').toLowerCase();
            if (!email) return;
            if (!dedup.has(email)) {
              dedup.set(email, {
                email,
                display_name: m?.display_name || email,
              });
            }
          });
          setManagers(Array.from(dedup.values()));
        })
        .catch(() => {});
    }
  }, [assignModal, isAdmin]); // eslint-disable-line

  if (loading) return <div className="idea-detail"><p>Loading…</p></div>;
  if (!idea) return <div className="idea-detail"><p>Idea not found.</p></div>;

  const permissionCtx = { role, userEmail, idea };
  const canEdit = canPerformAction({ ...permissionCtx, action: 'editIdea' });
  const canRate = canPerformAction({ ...permissionCtx, action: 'rate' });
  const canAssign = canPerformAction({ ...permissionCtx, action: 'assignManagers' });
  const canShortlist = canPerformAction({ ...permissionCtx, action: 'shortlist' });
  const canSendBack = canPerformAction({ ...permissionCtx, action: 'sendBack' });
  const canStartReview = isAdmin && idea.status === 'SUBMITTED';
  const canSubmit = isOwner && idea.submitted_by === userEmail && ['DRAFT', 'SENT_BACK'].includes(idea.status);
  const canRunAiAnalysis = (isManager || isAdmin) && ['UNDER_REVIEW', 'SHORTLISTED'].includes(idea.status);
  const canApprove = canPerformAction({ ...permissionCtx, action: 'approve' });
  const canClose = canPerformAction({ ...permissionCtx, action: 'close' });
  const canDelete = canPerformAction({ ...permissionCtx, action: 'delete' });

  const openStatusModal = (newStatus) => setStatusModal({ newStatus, reason: '' });
  const closeStatusModal = () => setStatusModal(null);

  const confirmStatusChange = async () => {
    if (!statusModal) return;
    try {
      await updateIdeaStatus(ideaId, { status: statusModal.newStatus, reason: statusModal.reason });
      showSuccess(`Status updated to ${statusModal.newStatus}`);
      closeStatusModal();
      reload();
    } catch (e) {
      showError(e?.response?.data?.error || 'Failed to update status');
    }
  };

  const handleAssign = async () => {
    try {
      await assignManagers(ideaId, selectedManagers);
      showSuccess('Managers assigned successfully');
      setAssignModal(false);
      reload();
    } catch (e) {
      showError(e?.response?.data?.error || 'Failed to assign managers');
    }
  };

  const handlePostComment = async () => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await addComment(ideaId, {
        content: commentText,
        visibility: isOwner ? 'all' : commentVisibility,
      });
      setCommentText('');
      showSuccess('Comment posted');
      reload();
    } catch {
      showError('Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const handleSaveRating = async () => {
    setSavingRating(true);
    try {
      await rateIdea(ideaId, rating);
      showSuccess('Rating saved');
      reload();
    } catch (e) {
      showError(e?.response?.data?.error || 'Failed to save rating');
    } finally {
      setSavingRating(false);
    }
  };

  const handleFlag = async () => {
    try {
      await flagIdea(ideaId, 'peer_review');
      showSuccess('Idea flagged for peer review');
      reload();
    } catch {
      showError('Failed to flag idea');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this idea permanently?')) return;
    try {
      await deleteIdea(ideaId);
      showSuccess('Idea deleted');
      navigate('/ideas');
    } catch {
      showError('Failed to delete idea');
    }
  };

  const handleSubmitIdea = async () => {
    try {
      await updateIdeaStatus(ideaId, { status: 'SUBMITTED' });
      showSuccess('Idea submitted for admin review');
      reload();
    } catch (e) {
      showError(e?.response?.data?.error || 'Failed to submit idea');
    }
  };

  const handleRunAiAnalysis = async () => {
    setRunningAnalysis(true);
    try {
      const prompt = [idea.title, idea.description].filter(Boolean).join('\n\n');
      const questionnaireData = idea.questionnaire_data || null;

      const feasibilityResult = await checkFeasibility(
        prompt || idea.title || '',
        !!questionnaireData,
        null
      );

      const parsed = parseFeasibilityResult(feasibilityResult);
      if (!parsed.valid) {
        showError(`Idea validation failed: ${parsed.recommendation || 'Please improve the idea and try again.'}`);
        return;
      }

      const reportResponse = await generateReport(
        'traditional',
        prompt || idea.title || '',
        true,
        'parallel',
        null,
        questionnaireData
      );

      if (reportResponse?.status === 'accepted' && reportResponse?.job_id) {
        showSuccess(`AI analysis started. Job ID: ${reportResponse.job_id}`);
        return;
      }

      showSuccess('AI analysis started successfully');
    } catch (e) {
      showError(e?.response?.data?.error || e?.message || 'Failed to run AI analysis');
    } finally {
      setRunningAnalysis(false);
    }
  };

  const submittedAt = idea.submitted_at || idea.created_at;
  const comments = idea.comments || [];
  const ratings  = idea.ratings  || [];
  const qData    = idea.questionnaire_data || {};
  const avgRating = (key) => {
    const vals = ratings.map(r => r[key]).filter(v => typeof v === 'number');
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '—';
  };

  return (
    <div className="idea-detail">
      <RoleViewTabs onChange={setRole} />

      <button className="idea-detail-back" onClick={() => navigate('/ideas')}>← Back to Ideas</button>

      {/* Header */}
      <div className="idea-detail-header">
        <div className="idea-detail-title-row">
          <span className="idea-detail-title">{idea.title}</span>
          <IdeaStatusBadge status={idea.status} />
        </div>
        <div className="idea-detail-meta">
          {idea.category && <span>📂 {idea.category.replace('_', '/')}</span>}
          {submittedAt && <span>📅 {new Date(submittedAt).toLocaleDateString()}</span>}
          {idea.submitted_by && <span>👤 {idea.submitted_by}</span>}
          {idea.assigned_managers?.length > 0 && (
            <span>👥 {idea.assigned_managers.join(', ')}</span>
          )}
        </div>
        {idea.tags?.length > 0 && (
          <div className="idea-detail-tags">
            {idea.tags.map(t => <span key={t} className="idea-detail-tag">{t}</span>)}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="idea-action-bar">
        {canEdit     && <button className="btn-action secondary" onClick={() => navigate(`/ideas/${ideaId}/edit`)}>✏️ Edit</button>}
        {canSubmit   && <button className="btn-action primary"   onClick={handleSubmitIdea}>🚀 Submit Idea</button>}
        {canAssign   && <button className="btn-action primary"   onClick={() => setAssignModal(true)}>👥 Assign Managers</button>}
        {canRunAiAnalysis && (
          <button className="btn-action secondary" onClick={handleRunAiAnalysis} disabled={runningAnalysis}>
            {runningAnalysis ? '⏳ Running AI Analysis…' : '🤖 Run AI Analysis'}
          </button>
        )}
        {canStartReview && <button className="btn-action primary" onClick={() => openStatusModal('UNDER_REVIEW')}>▶️ Start Review</button>}
        {canShortlist && <button className="btn-action success"  onClick={() => openStatusModal('SHORTLISTED')}>✅ Shortlist</button>}
        {canSendBack  && <button className="btn-action warning"  onClick={() => openStatusModal('SENT_BACK')}>↩️ Send Back</button>}
        {canApprove   && <button className="btn-action success"  onClick={() => openStatusModal('APPROVED')}>🎉 Approve</button>}
        {canClose     && <button className="btn-action danger"   onClick={() => openStatusModal('CLOSED')}>🚫 Close</button>}
        {canPerformAction({ ...permissionCtx, action: 'flagPeerReview' }) && (
          <button className="btn-action secondary" onClick={handleFlag}>🚩 Flag for Peer Review</button>
        )}
        {canDelete && <button className="btn-action danger" onClick={handleDelete}>🗑️ Delete</button>}
      </div>

      {/* Tabs */}
      <div className="idea-tabs">
        {['overview', 'questionnaire', 'comments', (isManager || isAdmin) && 'ratings'].filter(Boolean).map(tab => (
          <button
            key={tab}
            className={`idea-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {{
              overview: '📄 Overview',
              questionnaire: '📋 8QA',
              comments: `💬 Comments (${comments.length})`,
              ratings: `⭐ Ratings (${ratings.length})`,
            }[tab]}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="idea-section">
          <h3>Description</h3>
          <p style={{ color: '#374151', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
            {idea.description || <em style={{ color: '#9ca3af' }}>No description provided.</em>}
          </p>
          {idea.sent_back_reason && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff7ed', borderRadius: 8, borderLeft: '3px solid #f97316' }}>
              <strong style={{ fontSize: '0.85rem', color: '#c2410c' }}>Sent Back Reason:</strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.88rem', color: '#374151' }}>{idea.sent_back_reason}</p>
            </div>
          )}
        </div>
      )}

      {/* Questionnaire */}
      {activeTab === 'questionnaire' && (
        <div className="idea-section">
          <h3>Idea Questionnaire</h3>
          {QUESTIONS.map(q => (
            <div key={q.id} className="qa-item">
              <div className="qa-question">{q.question}</div>
              {qData[q.id] ? (
                <div className="qa-answer">{qData[q.id]}</div>
              ) : (
                <div className="qa-answer" style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not answered</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Comments */}
      {activeTab === 'comments' && (
        <div className="idea-section">
          <h3>Comments</h3>
          {comments.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '0.88rem' }}>No comments yet.</p>
          ) : (
            <div className="comment-list">
              {comments.map(c => (
                <div key={c.id} className="comment-item">
                  <div className="comment-header">
                    <span className="comment-author">
                      {c.author_email}
                      {c.author_role && <span style={{ marginLeft: 6, color: '#94a3b8', fontWeight: 400 }}>({c.author_role})</span>}
                      {c.is_ai_generated && <span className="comment-visibility-tag">AI</span>}
                      {c.visibility && c.visibility !== 'all' && (
                        <span className="comment-visibility-tag">{c.visibility}</span>
                      )}
                    </span>
                    <span className="comment-time">
                      {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <div className="comment-body">{c.content}</div>
                </div>
              ))}
            </div>
          )}

          {/* Add comment */}
          <div className="comment-input-area">
            <textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Add a comment…"
            />
            <div className="comment-input-row">
              {(isManager || isAdmin) && (
                <select
                  value={commentVisibility}
                  onChange={e => setCommentVisibility(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.82rem' }}
                >
                  <option value="all">Visible to all</option>
                  <option value="managers_only">Managers only</option>
                  <option value="admin_only">Admin only</option>
                </select>
              )}
              <button
                className="btn-action primary"
                onClick={handlePostComment}
                disabled={postingComment || !commentText.trim()}
              >
                {postingComment ? 'Posting…' : 'Post Comment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ratings */}
      {activeTab === 'ratings' && (isManager || isAdmin) && (
        <div className="idea-section">
          <h3>Ratings</h3>

          {/* Summary */}
          {ratings.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: '0.88rem', marginBottom: 10 }}>Average ({ratings.length} rating{ratings.length > 1 ? 's' : ''})</h3>
              {[
                { key: 'technical_feasibility', label: 'Technical Feasibility' },
                { key: 'business_viability',   label: 'Business Viability' },
                { key: 'impact',               label: 'Impact' },
              ].map(({ key, label }) => (
                <div key={key} className="rating-bar-row" style={{ marginBottom: 8 }}>
                  <span className="rating-bar-label">{label}</span>
                  <div className="rating-bar-track">
                    <div className="rating-bar-fill" style={{ width: `${(parseFloat(avgRating(key)) / 5) * 100}%` }} />
                  </div>
                  <span className="rating-value">{avgRating(key)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Individual ratings */}
          {ratings.map((r, i) => (
            <div key={i} className="rating-card">
              <div className="rating-header">👤 {r.manager_email} — {r.rated_at ? new Date(r.rated_at).toLocaleDateString() : ''}</div>
              <div className="rating-bars">
                {[
                  { key: 'technical_feasibility', label: 'Technical Feasibility' },
                  { key: 'business_viability',    label: 'Business Viability' },
                  { key: 'impact',                label: 'Impact' },
                ].map(({ key, label }) => (
                  <div key={key} className="rating-bar-row">
                    <span className="rating-bar-label">{label}</span>
                    <div className="rating-bar-track">
                      <div className="rating-bar-fill" style={{ width: `${((r[key] || 0) / 5) * 100}%` }} />
                    </div>
                    <span className="rating-value">{r[key] ?? '—'}</span>
                  </div>
                ))}
              </div>
              {r.notes && <p style={{ marginTop: 8, fontSize: '0.82rem', color: '#64748b' }}>{r.notes}</p>}
            </div>
          ))}

          {/* Rating form for assigned manager */}
          {canRate && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
              <h3>Your Rating</h3>
              <div className="rating-form">
                {[
                  { key: 'technical_feasibility', label: 'Technical Feasibility' },
                  { key: 'business_viability',    label: 'Business Viability' },
                  { key: 'impact',                label: 'Impact' },
                ].map(({ key, label }) => (
                  <div key={key} className="rating-slider-group">
                    <label>{label} <span className="rating-slider-value">{rating[key]} / 5</span></label>
                    <input
                      type="range" min={0} max={5} step={0.5}
                      value={rating[key]}
                      onChange={e => setRating(prev => ({ ...prev, [key]: parseFloat(e.target.value) }))}
                    />
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Notes (optional)</label>
                  <textarea
                    value={rating.notes}
                    onChange={e => setRating(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add any additional comments on your rating…"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.88rem', resize: 'vertical', minHeight: 60, boxSizing: 'border-box' }}
                  />
                </div>
                <button className="btn-action primary" onClick={handleSaveRating} disabled={savingRating}>
                  {savingRating ? 'Saving…' : 'Save Rating'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status change modal */}
      {statusModal && (
        <div className="modal-overlay" onClick={closeStatusModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Confirm: {statusModal.newStatus}</h3>
            {['SENT_BACK', 'CLOSED'].includes(statusModal.newStatus) && (
              <textarea
                placeholder={statusModal.newStatus === 'SENT_BACK' ? 'Reason for sending back…' : 'Reason for closing…'}
                value={statusModal.reason}
                onChange={e => setStatusModal(prev => ({ ...prev, reason: e.target.value }))}
              />
            )}
            <div className="modal-actions">
              <button className="btn-action secondary" onClick={closeStatusModal}>Cancel</button>
              <button className="btn-action primary" onClick={confirmStatusChange}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign managers modal */}
      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Assign Managers</h3>
            <div className="assign-manager-list">
              {managers.length === 0 ? (
                <p style={{ padding: 12, color: '#9ca3af', fontSize: '0.88rem' }}>No managers found.</p>
              ) : managers.map(m => (
                <label key={m.email} className="assign-manager-item">
                  <input
                    type="checkbox"
                    checked={selectedManagers.includes(m.email)}
                    onChange={e => {
                      if (e.target.checked) setSelectedManagers(prev => [...prev, m.email]);
                      else setSelectedManagers(prev => prev.filter(em => em !== m.email));
                    }}
                  />
                  <span>{m.display_name || m.email}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.78rem', marginLeft: 'auto' }}>{m.email}</span>
                </label>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn-action secondary" onClick={() => setAssignModal(false)}>Cancel</button>
              <button className="btn-action primary" onClick={handleAssign}>Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IdeaDetail;
